import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  Calendar,
  Compass,
  Crown,
  Heart,
  Loader2,
  Search,
  Settings,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useRoleContext } from '../hooks/useRoleContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CLIENT_DASHBOARD_HERO_TEST_ID,
  CLIENT_DASHBOARD_SECTIONS,
  CLIENT_DASHBOARD_TEST_ID,
  CLIENT_HERO_BROWSE_CTA_TEST_ID,
  CLIENT_PREMIUM_COMING_SOON_BADGE_TEST_ID,
  CLIENT_PREMIUM_PLACEHOLDER_TEST_ID,
  CLIENT_STATUS_SUMMARY_TEST_ID,
  PREMIUM_CLIENT_BENEFITS,
  buildClientStatusSummary,
} from '../lib/clientDashboardDisplay';
import { ROUTE_OWNER_DASHBOARD } from '../lib/roleContext';
import { showsOwnerDashboardNav } from '../lib/roleExperience';

const SECTION_ICONS = {
  favorites: Heart,
  requests: Calendar,
  appointments: Calendar,
  messages: Bell,
  journey: Compass,
  alerts: Search,
  profile: Settings,
};

const ACCENT_STYLES = {
  gold: 'border-l-military-gold bg-gradient-to-br from-amber-50/80 to-white',
  blue: 'border-l-signal-blue bg-gradient-to-br from-sky-50/70 to-white',
  slate: 'border-l-slate-300 bg-white',
  warm: 'border-l-amber-600/60 bg-gradient-to-br from-orange-50/50 to-white',
};

function DashboardCard({ section, t }) {
  const Icon = SECTION_ICONS[section.id] || Heart;
  const accent = ACCENT_STYLES[section.accent] || ACCENT_STYLES.slate;
  const body = (
    <div
      className={`rounded-xl border border-slate-200/80 border-l-4 p-5 h-full transition-all ${
        section.comingSoon
          ? 'opacity-90 cursor-default'
          : 'hover:border-signal-blue/35 hover:shadow-md cursor-pointer'
      } ${accent} ${section.featured ? 'sm:col-span-1' : ''}`}
      data-testid={section.testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-11 h-11 rounded-xl bg-white/80 border border-slate-200/60 flex items-center justify-center shrink-0 shadow-sm">
          <Icon className="w-5 h-5 text-signal-blue" />
        </div>
        {!section.comingSoon && (
          <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" aria-hidden />
        )}
      </div>
      <h2 className="text-base font-semibold text-slate-900 mt-4">
        {t(section.labelKey, section.id)}
      </h2>
      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
        {t(section.descKey, '')}
      </p>
      {section.comingSoon && (
        <Badge variant="outline" className="mt-3 text-[10px] border-slate-300 text-slate-500">
          {t('client.coming_soon', 'Coming soon')}
        </Badge>
      )}
    </div>
  );

  if (section.comingSoon || !section.href) {
    return body;
  }

  return <Link to={section.href} className="block h-full">{body}</Link>;
}

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { canAccessOwner, isGuestContext } = useRoleContext();
  const { t } = useLanguage();
  const showOwnerLink = showsOwnerDashboardNav({ canAccessOwner, isGuestContext });
  const statusMetrics = buildClientStatusSummary();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/dashboard');
    }
  }, [authLoading, user, navigate]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-white pt-6 pb-10" data-testid={CLIENT_DASHBOARD_TEST_ID}>
      <div className="max-w-6xl mx-auto px-4">
        <section
          className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-signal-blue/10 via-white to-military-gold/10 p-6 sm:p-8 mb-6 shadow-sm"
          data-testid={CLIENT_DASHBOARD_HERO_TEST_ID}
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-military-gold/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-signal-blue mb-2">
              {t('client.dashboard_kicker', 'Client home search')}
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
              {t('client.hero_title', 'Your Home Search')}
            </h1>
            <p className="text-slate-600 text-base sm:text-lg">
              {t(
                'client.hero_subtitle',
                'Saved homes, requests, appointments, and messages in one place.',
              )}
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button
                asChild
                className="bg-signal-blue hover:bg-signal-blue-hover text-white"
                data-testid={CLIENT_HERO_BROWSE_CTA_TEST_ID}
              >
                <Link to="/listings">
                  {t('client.nav_browse_homes', 'Browse Homes')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {showOwnerLink && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-amber-900">
              {t(
                'client.owner_tools_banner',
                'You also have owner tools. Incoming guest inquiries and listing management live in Owner Dashboard.',
              )}
            </p>
            <Button asChild variant="outline" className="border-amber-300 text-amber-900 shrink-0">
              <Link to={ROUTE_OWNER_DASHBOARD}>{t('nav.owner_dashboard', 'Owner Dashboard')}</Link>
            </Button>
          </div>
        )}

        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
          data-testid={CLIENT_STATUS_SUMMARY_TEST_ID}
        >
          {statusMetrics.map((metric) => (
            <div
              key={metric.id}
              className="rounded-xl border border-slate-200 bg-white/90 px-4 py-3 shadow-sm"
              data-testid={metric.testId}
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                {t(metric.labelKey, metric.defaultLabel)}
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{metric.value}</p>
            </div>
          ))}
        </div>

        <div className="mb-3">
          <h2 className="text-lg font-semibold text-slate-900">
            {t('client.dashboard_sections_title', 'Your search workspace')}
          </h2>
          <p className="text-sm text-slate-600">
            {t('client.dashboard_sections_subtitle', 'Jump back into saved homes, requests, and messages.')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {CLIENT_DASHBOARD_SECTIONS.map((section) => (
            <DashboardCard key={section.id} section={section} t={t} />
          ))}
        </div>

        <div
          className="relative overflow-hidden rounded-2xl border border-military-gold/30 bg-gradient-to-br from-amber-50 via-white to-slate-50 p-6 sm:p-7"
          data-testid={CLIENT_PREMIUM_PLACEHOLDER_TEST_ID}
        >
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-military-gold/15 rounded-full blur-2xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-military-gold/15 border border-military-gold/25 flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-military-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  {t('client.premium_tools_title', 'Premium Client Tools')}
                  <Crown className="w-4 h-4 text-military-gold" />
                </h2>
                <Badge
                  className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]"
                  data-testid={CLIENT_PREMIUM_COMING_SOON_BADGE_TEST_ID}
                >
                  {t('client.coming_soon', 'Coming soon')}
                </Badge>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">
                {t(
                  'client.premium_intro',
                  'Premium client tools are designed to help you find housing faster with early listing awareness, saved-search alerts, enhanced location insights, and relocation guidance.',
                )}
              </p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                {PREMIUM_CLIENT_BENEFITS.map((benefit) => (
                  <li
                    key={benefit.id}
                    className="flex items-start gap-2 text-sm text-slate-700"
                    data-testid={`client-premium-benefit-${benefit.id}`}
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-military-gold shrink-0" />
                    {t(benefit.labelKey, benefit.defaultLabel)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
